import React, { useState } from 'react';
import {
  LayoutDashboard,
  Globe,
  Users,
  ChevronLeft,
  ChevronRight,
  Wallet,
  ChevronDown,
  ChevronUp,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw,
  Ticket,
  FileText,
  Settings,
  Share2,
  LifeBuoy,
  UserCheck,
  DollarSign,
  Gamepad2,
  Activity,
  BarChart3,
  LayoutList,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import styles from './sidebar.module.scss';
import { useAppSelector } from 'store/hooks';
import { useMediaQuery } from '@mui/material';

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

interface MenuItem {
  path?: string;
  label: string;
  icon: any;
  end?: boolean;
  children?: { path: string; label: string; icon: any; end?: boolean }[];
}

const menuItems: MenuItem[] = [
  { path: '/', label: 'Statistics', icon: LayoutDashboard, end: true },
  { path: '/sites', label: 'Sites', icon: Globe },
  {
    label: 'Clients',
    icon: Users,
    children: [
      { path: '/clients/online', label: 'Online clients', icon: UserCheck },

      { path: '/clients', label: 'Client lists', icon: Users, end: true },
      { path: '/clients/blocked', label: 'Blocked clients', icon: UserCheck }, // Reused UserCheck or another icon
    ],
  },
  { path: '/promocodes', label: 'Promo codes', icon: Ticket },
  {
    label: 'Payments',
    icon: Wallet,
    children: [
      { path: '/finances/deposit', label: 'Deposit', icon: ArrowUpCircle },
      { path: '/finances/withdraw', label: 'Withdraw', icon: ArrowDownCircle },
      { path: '/payments', label: 'Providers', icon: DollarSign },
    ],
  },
  {
    label: 'Games',
    icon: Gamepad2,
    children: [
      { path: '/games/sessions', label: 'Active session', icon: Activity },
      { path: '/games/statistics', label: 'Game Statistics', icon: BarChart3 },
      { path: '/games/providers', label: 'Providers', icon: LayoutList },
      { path: '/games/sync', label: 'Sync Games', icon: RefreshCw },
    ],
  },

  {
    label: 'Management',
    icon: Settings,
    children: [{ path: '/management/managers', label: 'Managers', icon: LifeBuoy }],
  },
  { path: '/social-links', label: 'Social links', icon: Share2 },
  { path: '/logs', label: 'Logs', icon: FileText },
];

const Sidebar: React.FC<SidebarProps> = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const { total: pendingWithdrawTotal } = useAppSelector(
    (state) => state.transactions.pendingWithdraw,
  );
  const isMobile = useMediaQuery('(max-width:768px)');

  const handleItemClick = () => {
    if (isMobile) {
      setCollapsed(true);
    }
  };

  const toggleMenu = (label: string) => {
    if (collapsed) {
      setCollapsed(false);
      setOpenMenus([label]);
      return;
    }
    setOpenMenus((prev) => (prev.includes(label) ? [] : [label]));
  };

  const isChildActive = (children?: { path: string }[]) => {
    if (!children) return false;
    return children.some((child) => location.pathname === child.path);
  };

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
      <div className={styles.logoContainer}>
        {!collapsed && (
          <h2 className={styles.logo}>
            <span className={styles.winLogo}>Dgenta</span>
            <span className={styles.winLogo}>System</span>
          </h2>
        )}
        <button
          className={styles.collapseBtn}
          onClick={() => setCollapsed(!collapsed)}
          aria-label="Toggle Sidebar"
        >
          {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      <nav className={styles.nav}>
        <ul className={styles.menuList}>
          {menuItems.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const isOpen = openMenus.includes(item.label);
            const isActiveParent = isChildActive(item.children);

            if (hasChildren) {
              return (
                <li key={item.label} className={styles.menuItem}>
                  <div
                    className={`${styles.menuLink} ${isActiveParent ? styles.active : ''} ${
                      styles.parentLink
                    }`}
                    onClick={() => toggleMenu(item.label)}
                  >
                    <div className={styles.iconWrapper}>
                      <item.icon size={20} className={styles.icon} />
                      {collapsed && item.label === 'Payments' && pendingWithdrawTotal > 0 && (
                        <div className={styles.collapsedBadge}>{pendingWithdrawTotal}</div>
                      )}
                    </div>
                    {!collapsed && (
                      <>
                        <div className={styles.labelWrapper}>
                          <span className={styles.label}>{item.label}</span>
                          {item.label === 'Payments' && !isOpen && pendingWithdrawTotal > 0 && (
                            <div className={styles.badge}>{pendingWithdrawTotal}</div>
                          )}
                        </div>
                        <div className={styles.chevron}>
                          {isOpen ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                        </div>
                      </>
                    )}
                  </div>
                  {!collapsed && isOpen && (
                    <ul className={styles.subMenu}>
                      {item.children!.map((child) => (
                        <li key={child.path} className={styles.subMenuItem}>
                          <NavLink
                            to={child.path}
                            className={({ isActive }) =>
                              `${styles.subMenuLink} ${isActive ? styles.active : ''}`
                            }
                            end={child.end}
                            onClick={handleItemClick}
                          >
                            <child.icon size={16} className={styles.subIcon} />
                            <div className={styles.subLabelWrapper}>
                              <span className={styles.subLabel}>{child.label}</span>
                              {child.label === 'Withdraw' && pendingWithdrawTotal > 0 && (
                                <div className={styles.badge}>{pendingWithdrawTotal}</div>
                              )}
                            </div>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            }

            return (
              <li key={item.path} className={styles.menuItem}>
                <NavLink
                  to={item.path!}
                  className={({ isActive }) =>
                    `${styles.menuLink} ${isActive ? styles.active : ''}`
                  }
                  end={item.end}
                  onClick={() => {
                    setOpenMenus([]);
                    handleItemClick();
                  }}
                >
                  <div className={styles.iconWrapper}>
                    <item.icon size={20} className={styles.icon} />
                  </div>
                  {!collapsed && <span className={styles.label}>{item.label}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
